/* ============================================================
   TV LIGHTBOX — assets/tv-lightbox.js
   Attaches to every <img> inside any element with class "tv-gallery".
   Features: overlay, prev/next arrows, counter, keyboard arrows,
   ESC / backdrop / X to close, touch swipe to navigate,
   click-to-zoom with drag panning.
   Include in layout/theme.liquid before </body>:
   <script src="{{ 'tv-lightbox.js' | asset_url }}" defer></script>
   ============================================================ */
(function () {
  'use strict';

  var images = [];
  var current = 0;
  var overlay, imgEl, counterEl;
  var zoomed = false;
  var panX = 0, panY = 0, startX = 0, startY = 0, dragging = false;
  var touchStartX = null;

  function fullSrc(img) {
    var src = img.getAttribute('data-full') || img.currentSrc || img.src;
    /* strip Shopify width params so the lightbox gets the master file */
    src = src.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    /* strip legacy _WxH size suffixes e.g. image_600x600.jpg */
    src = src.replace(/_(\d+x\d*|x\d+)(\.[a-zA-Z]+)(\?|$)/, '$2$3');
    return src;
  }

  function buildOverlay() {
    overlay = document.createElement('div');
    overlay.className = 'tv-lb';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Image viewer');
    overlay.innerHTML =
      '<button class="tv-lb__close" aria-label="Close">' +
        '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
      '</button>' +
      '<button class="tv-lb__nav tv-lb__prev" aria-label="Previous image">' +
        '<svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"></polyline></svg>' +
      '</button>' +
      '<div class="tv-lb__imgwrap"><img class="tv-lb__img" alt=""></div>' +
      '<button class="tv-lb__nav tv-lb__next" aria-label="Next image">' +
        '<svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>' +
      '</button>' +
      '<div class="tv-lb__counter"></div>';
    document.body.appendChild(overlay);

    imgEl = overlay.querySelector('.tv-lb__img');
    counterEl = overlay.querySelector('.tv-lb__counter');

    overlay.querySelector('.tv-lb__close').addEventListener('click', close);
    overlay.querySelector('.tv-lb__prev').addEventListener('click', function (e) { e.stopPropagation(); prev(); });
    overlay.querySelector('.tv-lb__next').addEventListener('click', function (e) { e.stopPropagation(); next(); });

    /* backdrop click closes (only when the click is the backdrop itself) */
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay || e.target.classList.contains('tv-lb__imgwrap')) close();
    });

    /* click-to-zoom, drag-to-pan */
    imgEl.addEventListener('click', function (e) {
      e.stopPropagation();
      if (dragging) return;
      toggleZoom(e);
    });
    imgEl.addEventListener('pointerdown', function (e) {
      if (!zoomed) return;
      dragging = false;
      startX = e.clientX - panX;
      startY = e.clientY - panY;
      imgEl.setPointerCapture(e.pointerId);
      imgEl.addEventListener('pointermove', onPan);
    });
    imgEl.addEventListener('pointerup', function (e) {
      imgEl.removeEventListener('pointermove', onPan);
      /* allow the click handler to distinguish drag from click */
      setTimeout(function () { dragging = false; }, 0);
    });

    /* touch swipe to navigate (when not zoomed) */
    overlay.addEventListener('touchstart', function (e) {
      if (zoomed) return;
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    overlay.addEventListener('touchend', function (e) {
      if (zoomed || touchStartX === null) return;
      var dx = e.changedTouches[0].clientX - touchStartX;
      if (dx > 50) prev();
      else if (dx < -50) next();
      touchStartX = null;
    }, { passive: true });

    document.addEventListener('keydown', onKey);
  }

  function onPan(e) {
    dragging = true;
    panX = e.clientX - startX;
    panY = e.clientY - startY;
    applyTransform();
  }

  function applyTransform() {
    imgEl.style.transform = zoomed
      ? 'translate(' + panX + 'px,' + panY + 'px) scale(2.2)'
      : '';
  }

  function toggleZoom(e) {
    zoomed = !zoomed;
    if (zoomed) {
      /* zoom toward the clicked point */
      var rect = imgEl.getBoundingClientRect();
      var ox = ((e.clientX - rect.left) / rect.width) * 100;
      var oy = ((e.clientY - rect.top) / rect.height) * 100;
      imgEl.style.transformOrigin = ox + '% ' + oy + '%';
      overlay.classList.add('tv-lb--zoomed');
    } else {
      panX = panY = 0;
      overlay.classList.remove('tv-lb--zoomed');
    }
    applyTransform();
  }

  function resetZoom() {
    zoomed = false;
    panX = panY = 0;
    overlay.classList.remove('tv-lb--zoomed');
    applyTransform();
  }

  function show(i) {
    current = (i + images.length) % images.length;
    resetZoom();
    var original = images[current].currentSrc || images[current].src;
    imgEl.onerror = function () {
      /* master-file guess 404'd — fall back to the original URL */
      imgEl.onerror = null;
      imgEl.src = original;
    };
    imgEl.src = fullSrc(images[current]);
    imgEl.alt = images[current].alt || '';
    counterEl.textContent = (current + 1) + ' / ' + images.length;
    /* preload neighbours */
    [current + 1, current - 1].forEach(function (n) {
      var p = new Image();
      p.src = fullSrc(images[(n + images.length) % images.length]);
    });
  }

  function open(i) {
    if (!overlay) buildOverlay();
    show(i);
    overlay.classList.add('tv-lb--open');
    document.documentElement.classList.add('tv-lb-lock');
  }

  function close() {
    overlay.classList.remove('tv-lb--open');
    document.documentElement.classList.remove('tv-lb-lock');
    resetZoom();
  }

  function next() { show(current + 1); }
  function prev() { show(current - 1); }

  function onKey(e) {
    if (!overlay || !overlay.classList.contains('tv-lb--open')) return;
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowRight') next();
    else if (e.key === 'ArrowLeft') prev();
  }

  function init() {
    var galleries = document.querySelectorAll('.tv-gallery');
    if (!galleries.length) return;
    galleries.forEach(function (g) {
      g.querySelectorAll('img').forEach(function (img) {
        images.push(img);
        var idx = images.length - 1;
        img.classList.add('tv-gallery__img');
        img.addEventListener('click', function (e) {
          e.preventDefault();
          open(idx);
        });
        /* if the image is wrapped in a link, don't follow it */
        var a = img.closest('a');
        if (a) a.addEventListener('click', function (e) { e.preventDefault(); });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();